import { createContext, useContext, useState, useRef, useCallback } from 'react';

/**
 * useConfirm() — returns an async ask({title, message, confirmLabel, danger}) that
 * resolves to true/false. Replaces window.confirm(), which is unreliable on
 * mobile Safari (especially in standalone/PWA mode where it can be silently
 * blocked) and on Android Chrome inside iframes.
 *
 * Wrap the app in <ConfirmProvider> once; call useConfirm() anywhere.
 */

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [opts, setOpts] = useState(null);    // { title, message, confirmLabel, danger }
  const resolverRef = useRef(null);

  const ask = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setOpts({
        title: options.title ?? 'Confirm',
        message: options.message ?? 'Are you sure?',
        confirmLabel: options.confirmLabel ?? 'Confirm',
        danger: options.danger ?? false,
      });
    });
  }, []);

  function settle(v) {
    setOpts(null);
    if (resolverRef.current) {
      resolverRef.current(v);
      resolverRef.current = null;
    }
  }

  return (
    <ConfirmContext.Provider value={ask}>
      {children}
      {opts && (
        <div className="modal-backdrop" onClick={() => settle(false)}>
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 420, borderRadius: 'var(--r3)', alignSelf: 'center', margin: '0 16px' }}
          >
            <h2 style={{ marginTop: 0 }}>{opts.title}</h2>
            <div style={{ marginBottom: 24, color: 'var(--text2)' }}>{opts.message}</div>
            <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => settle(false)} autoFocus>Cancel</button>
              <button
                onClick={() => settle(true)}
                className={opts.danger ? 'danger' : 'primary'}
                style={opts.danger ? { background: 'var(--red)', borderColor: 'var(--red)', color: 'white' } : undefined}
              >
                {opts.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ask = useContext(ConfirmContext);
  if (!ask) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return ask;
}
